import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { BaseFieldProps } from './types'
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group'
import clsx from 'clsx'
import { useMemo } from 'react'
import { Label } from '@/components/shadcn/label'
import { DocumentationButton } from './DocumentationButton'

export const RadioField: React.FC<BaseFieldProps> = ({ field, formFieldName, control, variant }) => {
  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => <RadioFieldInner field={field} formField={formField} variant={variant} />}
    />
  )
}

const RadioFieldInner: React.FC<{
  field: BaseFieldProps['field']
  formField: { value: unknown; onChange: (v: unknown) => void }
  variant: BaseFieldProps['variant']
}> = ({ field, formField, variant }) => {
  const transformedChoices = useMemo(() => {
    return field.choices?.map((choice) => {
      const value = typeof choice === 'string' || typeof choice === 'number' ? String(choice) : choice.value
      const label = typeof choice === 'string' || typeof choice === 'number' ? String(choice) : choice.title
      return {
        value,
        label,
      }
    })
  }, [field.choices])

  if (variant === 'row') {
    return (
      <FormItem className="flex flex-1 translate-y-[calc((var(--page-header-height,57px)-1.5rem)/2)] flex-col self-end">
        <FormControl>
          <RadioGroup
            onValueChange={formField.onChange}
            disabled={field.readOnly}
            value={formField.value as string}
            className="flex flex-row items-end gap-0"
          >
            {transformedChoices?.map((choice, index) => {
              const choiceValue = choice.value as string
              const itemId = `${field.id}-${choiceValue}`
              const isSelected = formField.value === choiceValue

              return (
                <div key={choiceValue} className={clsx('-mb-px flex items-end', { '-ml-px': index > 0 })}>
                  <RadioGroupItem id={itemId} value={choiceValue} className="sr-only" />
                  <Label
                    htmlFor={itemId}
                    className={clsx(
                      'flex h-6 cursor-pointer items-center rounded-t border border-b-2 border-border border-b-transparent bg-offwhite px-4 text-sm font-normal transition-colors',
                      {
                        'border-b-primary bg-background font-bold text-foreground': isSelected,
                        'text-muted-foreground': !isSelected,
                        'cursor-not-allowed opacity-50': field.readOnly,
                      }
                    )}
                  >
                    {choice.label}
                  </Label>
                </div>
              )
            })}
          </RadioGroup>
        </FormControl>
        <FormMessage />
      </FormItem>
    )
  }

  return (
    <FormItem className={clsx('flex flex-1 flex-col')}>
      <div className="flex justify-start gap-2">
        <FormLabel>{field.title}</FormLabel>
        <DocumentationButton documentation={field.documentation} />
      </div>
      <FormDescription className="text-xs">{field.description}</FormDescription>
      <FormControl>
        <RadioGroup
          onValueChange={formField.onChange}
          disabled={field.readOnly}
          value={formField.value as string}
          className="flex flex-col gap-2"
        >
          {transformedChoices?.map((choice) => {
            const choiceValue = choice.value as string
            const itemId = `${field.id}-${choiceValue}`

            return (
              // <div key={choice.value} className="flex items-stretch gap-2 rounded-md border">
              <div key={choiceValue} className="flex items-stretch gap-2 border">
                <div className="flex shrink-0 items-center justify-center px-3 py-4">
                  <RadioGroupItem id={itemId} value={choiceValue} />
                </div>
                <Label className="flex flex-1 items-center" htmlFor={itemId}>
                  {choice.label}
                </Label>
              </div>
            )
          })}
        </RadioGroup>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}
